import {
  deletePromptColumn,
  deleteReviewUuid,
  getReviewByUid,
  updateExtractionByUid,
  type ReviewExtractionInDb,
  type ReviewExtractionUpdateUser,
  type ReviewInDb,
} from '@/api';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth';

/**
 * Store to manage review data and related operations
 */
export const useReviewStore = defineStore('review', {
  state: () => ({
    current: null as ReviewInDb | null,
    pdfFileUid: null as string | null,
    pdfRevisionUid: null as string | null,
    pdfSource: null as string | null,
    loading: false,
    error: null as string | null,
    isPdf: true, // Track whether the file is a PDF
    isDocx: false, // Track whether the file is a DOCX
  }),
  getters: {
    fileName: (state): string => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      return state.current?.name || 'document.pdf';
    },
  },
  actions: {
    /**
     * Fetches the file associated with the current review
     * @returns Promise<boolean> - true if fetch succeeded, false otherwise
     */
    async fetchPdf(): Promise<boolean> {
      if (!this.pdfRevisionUid) return false;

      try {
        this.loading = true;
        this.isPdf = true;
        this.isDocx = false;

        const fileExtension = this.fileName.split('.').pop()?.toLowerCase();
        const authStore = useAuthStore();
        const token = await authStore.user?.getIdToken();
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const url = `${apiUrl}/v1/app/repository/vault/file/revision/${this.pdfRevisionUid}`;

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        if (fileExtension === 'docx') {
          this.isPdf = false;
          this.isDocx = true;
          const arrayBuffer = await response.arrayBuffer();
          const blob = new Blob([arrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
          this.pdfSource = URL.createObjectURL(blob);
          return true;
        }

        if (fileExtension && fileExtension !== 'pdf') {
          this.isPdf = false;
          const arrayBuffer = await response.arrayBuffer();
          this.pdfSource = URL.createObjectURL(new Blob([arrayBuffer]));
          return true;
        }

        const arrayBuffer = await response.arrayBuffer();
        const signature = new TextDecoder().decode(new Uint8Array(arrayBuffer, 0, 5));
        if (signature.startsWith('%PDF')) {
          this.pdfSource = URL.createObjectURL(
            new Blob([arrayBuffer], { type: 'application/pdf' })
          );
          this.isPdf = true;
        } else {
          this.isPdf = false;
        }
        return true;
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch file';
        this.isPdf = false;
        return false;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Gets a review by its UUID using the direct endpoint
     * @param reviewUid - The UUID of the review to fetch
     * @returns Promise<ReviewInDb | null> - The review or null if not found
     */
    async getReviewByUid(reviewUid: string): Promise<ReviewInDb | null> {
      try {
        this.loading = true;
        this.error = null;

        const response = await getReviewByUid({ path: { review_uid: reviewUid } });

        if (!response.data) {
          throw new Error('Failed to fetch review');
        }
        const review = response.data;
        this.current = review;

        return review;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
          errorMessage = apiError.detail || apiError.message || 'Failed to fetch review';
        } else {
          errorMessage = 'Failed to fetch review';
        }

        this.error = errorMessage;
        return null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Deletes a prompt column from a review
     * @param reviewUid - The UUID of the review
     * @param columnPromptUid - The UUID of the prompt column to delete
     * @returns Promise<boolean> - true if delete succeeded, false otherwise
     */
    async deletePromptColumn(reviewUid: string, columnPromptUid: string): Promise<boolean> {
      try {
        this.loading = true;
        this.error = null;

        await deletePromptColumn({
          path: {
            review_uid: reviewUid,
          },
          query: {
            column_prompt_uid: columnPromptUid,
          },
        });

        return true;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          errorMessage =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
            apiError.detail ||
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
            apiError.message ||
            'Failed to delete prompt column. Please try again.';
        } else {
          errorMessage = 'Failed to delete prompt column. Please try again.';
        }

        this.error = errorMessage;
        return false;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Updates an extraction's approval status
     * @param reviewUid - The UUID of the review
     * @param fileReviewUid - The UUID of the file review
     * @param extractionUid - The UUID of the extraction to update
     * @param approved - The approval status to set
     * @returns Promise<ReviewExtractionInDb | null> - The updated extraction or null if failed
     */
    async updateExtractionByUid(
      reviewUid: string,
      fileReviewUid: string,
      extractionUid: string,
      approved: boolean
    ): Promise<ReviewExtractionInDb | null> {
      try {
        this.loading = true;
        this.error = null;

        const updateData: ReviewExtractionUpdateUser = { approved };
        const response = await updateExtractionByUid({
          path: {
            review_uid: reviewUid,
            file_review_uid: fileReviewUid,
            extraction_uid: extractionUid,
          },
          body: updateData,
        });

        if (!response.data) {
          throw new Error('Failed to update extraction approval status');
        }
        const updatedExtraction = response.data;
        return updatedExtraction;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          errorMessage =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
            apiError.detail ||
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
            apiError.message ||
            'Failed to update extraction approval status. Please try again.';
        } else {
          errorMessage = 'Failed to update extraction approval status. Please try again.';
        }

        this.error = errorMessage;
        return null;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Resets the store state
     */
    reset(): void {
      this.current = null;
      this.pdfFileUid = null;
      if (this.pdfSource) {
        URL.revokeObjectURL(this.pdfSource);
      }
      this.pdfSource = null;
      this.loading = false;
      this.error = null;
      this.isPdf = true; // Reset to default state
      this.isDocx = false; // Reset DOCX state
    },

    /**
     * Deletes a review by its UUID
     * @param reviewUid - The UUID of the review to delete
     * @returns Promise<boolean> - true if delete succeeded, false otherwise
     */
    async deleteReview(reviewUid: string): Promise<boolean> {
      try {
        this.loading = true;
        this.error = null;

        await deleteReviewUuid({ path: { review_uid: reviewUid } });

        return true;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          errorMessage =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
            apiError.detail || apiError.message || 'Failed to delete review. Please try again.';
        } else {
          errorMessage = 'Failed to delete review. Please try again.';
        }

        this.error = errorMessage;
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
});
